import { useState } from 'react'
import { router } from '@inertiajs/react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Button } from '@/components/ui/Button'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import type Visitor from '@/types/serializers/Visitor'

interface ContactFormModalProps {
  mode: 'create' | 'edit'
  contact?: Visitor
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ContactFormModal({ mode, contact, open, onOpenChange }: ContactFormModalProps) {
  const { withAccount } = useRouteParams()
  const [name, setName] = useState(contact?.name || '')
  const [email, setEmail] = useState(contact?.email || '')
  const [phone, setPhone] = useState(contact?.phone || '')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    const data = {
      name: name || undefined,
      email: email || undefined,
      phone: phone || undefined,
    }

    if (mode === 'create') {
      router.post(routes.contacts.create.path(withAccount()), data, {
        onFinish: () => {
          setSubmitting(false)
          onOpenChange(false)
          setName('')
          setEmail('')
          setPhone('')
        },
      })
    } else if (contact) {
      router.patch(routes.contacts.update.path(withAccount({ id: contact.id })), data, {
        onFinish: () => {
          setSubmitting(false)
          onOpenChange(false)
        },
      })
    }
  }

  const title = mode === 'create' ? 'New Contact' : 'Edit Contact'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Add a new contact manually.'
              : 'Update contact details.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} autoComplete="off" data-1p-ignore>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="contact-name">Name</Label>
              <Input
                id="contact-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                autoComplete="off"
                data-1p-ignore
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-email">Email</Label>
              <Input
                id="contact-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@example.com"
                autoComplete="off"
                data-1p-ignore
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-phone">Phone</Label>
              <Input
                id="contact-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 (555) 000-0000"
                autoComplete="off"
                data-1p-ignore
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="cursor-pointer"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="cursor-pointer">
              {submitting ? 'Saving...' : mode === 'create' ? 'Create' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
